import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createViewing,
  updateViewingStatus,
  rescheduleViewing,
  getViewingsForLandlord,
  getViewingsForTenant,
  subscribeToLandlordViewings,
  subscribeToTenantViewings,
  generateAvailableSlots,
  setAvailability as setAvailabilityService,
  getAvailability,
  checkViewingOverlap,
  canCancelViewing
} from '../services/viewings.service';

/**
 * Hook voor landlord viewings
 * @param {string} [status] - Filter op status
 * @returns {Object} - { viewings, loading, error, actions }
 */
export const useLandlordViewings = (status = null) => {
  const { user } = useAuth();
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !user.uid) {
      console.log('No user, clearing viewings');
      setViewings([]);
      setLoading(false);
      return;
    }

    console.log('Setting up landlord viewings subscription for user:', user.uid, 'status:', status);
    setLoading(true);
    setError(null);

    let unsubscribe;
    
    const successCallback = (viewingsData) => {
      setViewings(viewingsData || []);
      setLoading(false);
    };

    const errorCallback = (error) => {
      if (!error) return;
      console.error('Error loading landlord viewings:', error);
      setError(error.message || 'Fout bij laden van bezichtigingen');
      setViewings([]);
      setLoading(false);
    };

    let resolved = false;
    
    try {
      unsubscribe = subscribeToLandlordViewings(
        user.uid, 
        status, 
        (data) => { resolved = true; successCallback(data); },
        errorCallback
      );
      
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          console.warn('Landlord viewings subscription timeout after 5 seconds');
          setViewings([]);
          setLoading(false);
          setError('Timeout bij laden van bezichtigingen');
        }
      }, 5000);

      return () => {
        clearTimeout(timeoutId);
        if (unsubscribe) {
          unsubscribe();
        }
      };
    } catch (error) {
      console.error('Error setting up landlord viewings subscription:', error);
      setError(error.message || 'Fout bij laden van bezichtigingen');
      setViewings([]);
      setLoading(false);
      return () => {};
    }
  }, [user, status]);

  const confirmViewing = useCallback(async (viewingId) => {
    try {
      await updateViewingStatus(viewingId, 'confirmed');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const declineViewing = useCallback(async (viewingId, reason = '') => {
    try {
      await updateViewingStatus(viewingId, 'declined', reason);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const cancelViewing = useCallback(async (viewingId, reason = '') => {
    try {
      await updateViewingStatus(viewingId, 'cancelled', reason);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const markCompleted = useCallback(async (viewingId) => {
    try {
      await updateViewingStatus(viewingId, 'completed');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const markNoShow = useCallback(async (viewingId) => {
    try {
      await updateViewingStatus(viewingId, 'no_show');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  const reschedule = useCallback(async (viewingId, newStartAt, newEndAt) => {
    try {
      await rescheduleViewing(viewingId, newStartAt, newEndAt);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  return {
    viewings,
    loading,
    error,
    actions: {
      confirmViewing,
      declineViewing,
      cancelViewing,
      markCompleted,
      markNoShow,
      reschedule
    }
  };
};

/**
 * Hook voor tenant viewings
 * @param {string} [status] - Filter op status
 * @returns {Object} - { viewings, loading, error, actions }
 */
export const useTenantViewings = (status = null) => {
  const { user } = useAuth();
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !user.uid) {
      console.log('No user, clearing viewings');
      setViewings([]);
      setLoading(false);
      return;
    }

    console.log('Setting up tenant viewings subscription for user:', user.uid, 'status:', status);
    setLoading(true);
    setError(null);

    let unsubscribe;
    let hasCalled = false; // Prevent multiple calls
    
    const successCallback = (viewingsData) => {
      if (hasCalled) {
        console.log('Callback already called, ignoring');
        return;
      }
      hasCalled = true;
      console.log('Viewings loaded:', viewingsData?.length || 0);
      setViewings(viewingsData || []);
      setLoading(false);
    };

    const errorCallback = (error) => {
      if (hasCalled) {
        console.log('Callback already called, ignoring error');
        return;
      }
      hasCalled = true;
      console.error('Error loading tenant viewings:', error);
      setError(error.message || 'Fout bij laden van bezichtigingen');
      setViewings([]);
      setLoading(false);
    };
    
    try {
      unsubscribe = subscribeToTenantViewings(
        user.uid, 
        status, 
        successCallback,
        errorCallback
      );
      
      // Timeout fallback - als na 5 seconden nog geen data, stop loading
      const timeoutId = setTimeout(() => {
        if (!hasCalled) {
          console.warn('Viewings subscription timeout after 5 seconds');
          hasCalled = true;
          setViewings([]);
          setLoading(false);
          setError('Timeout bij laden van bezichtigingen');
        }
      }, 5000);

      return () => {
        clearTimeout(timeoutId);
        if (unsubscribe) {
          unsubscribe();
        }
      };
    } catch (error) {
      console.error('Error setting up tenant viewings subscription:', error);
      setError(error.message || 'Fout bij laden van bezichtigingen');
      setViewings([]);
      setLoading(false);
      return () => {};
    }
  }, [user, status]);

  const requestViewing = useCallback(async (viewingData) => {
    try {
      const viewingId = await createViewing({
        ...viewingData,
        tenantUid: user.uid,
        requestedBy: 'tenant'
      });
      return viewingId;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [user]);

  const cancelViewing = useCallback(async (viewingId, reason = '') => {
    try {
      await updateViewingStatus(viewingId, 'cancelled', reason);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, []);

  return {
    viewings,
    loading,
    error,
    actions: {
      requestViewing,
      cancelViewing
    }
  };
};

/**
 * Hook voor availability management
 * @returns {Object} - { availability, loading, error, actions }
 */
export const useAvailability = () => {
  const { user } = useAuth();
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAvailability = useCallback(async () => {
    if (!user?.uid) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getAvailability(user.uid);
      setAvailability(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setAvailability([]);
      setLoading(false);
      return;
    }
    loadAvailability();
  }, [user, loadAvailability]);

  const saveAvailability = useCallback(async (availabilityRules) => {
    try {
      await setAvailabilityService(user.uid, availabilityRules);
      await loadAvailability();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [user, loadAvailability]);

  return {
    availability,
    loading,
    error,
    actions: {
      saveAvailability,
      reload: loadAvailability
    }
  };
};

/**
 * Hook voor available slots
 * @param {string} landlordUid 
 * @param {string} listingId - Property ID
 * @param {Date} date 
 * @returns {Object} - { slots, loading, error, actions }
 */
export const useAvailableSlots = (landlordUid, listingId, date) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSlots = useCallback(async () => {
    if (!landlordUid || !listingId || !date) {
      setSlots([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const availableSlots = await generateAvailableSlots(landlordUid, listingId, date);
      setSlots(availableSlots || []);
    } catch (error) {
      console.error('Error loading available slots:', error);
      setError(error.message || 'Fout bij laden van beschikbare tijden');
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [landlordUid, listingId, date]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  return {
    slots,
    loading,
    error,
    actions: {
      reload: loadSlots
    }
  };
};

/**
 * Hook voor viewing overlap check
 * @returns {Object} - { checkOverlap }
 */
export const useViewingOverlap = () => {
  const checkOverlap = useCallback(async (landlordUid, startAt, endAt, excludeViewingId = null) => {
    try {
      return await checkViewingOverlap(landlordUid, startAt, endAt, excludeViewingId);
    } catch (error) {
      console.error('Error checking overlap:', error);
      throw error;
    }
  }, []);

  return { checkOverlap };
};

/**
 * Hook voor viewing utilities
 * @returns {Object} - { canCancel, formatTime, formatDate, formatStatus }
 */
export const useViewingUtils = () => {
  const canCancel = useCallback((viewing) => {
    return canCancelViewing(viewing);
  }, []);

  const formatTime = useCallback((time) => {
    const date = time.toDate ? time.toDate() : new Date(time);
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const formatDate = useCallback((time) => {
    const date = time.toDate ? time.toDate() : new Date(time);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const formatStatus = useCallback((status) => {
    const statusMap = {
      pending: 'In afwachting',
      confirmed: 'Bevestigd',
      declined: 'Afgewezen',
      cancelled: 'Geannuleerd',
      completed: 'Voltooid',
      no_show: 'Niet verschenen'
    };
    return statusMap[status] || status;
  }, []);

  const getStatusColor = useCallback((status) => {
    const colorMap = {
      pending: 'text-yellow-600 bg-yellow-100',
      confirmed: 'text-green-600 bg-green-100',
      declined: 'text-red-600 bg-red-100',
      cancelled: 'text-gray-600 bg-gray-100',
      completed: 'text-blue-600 bg-blue-100',
      no_show: 'text-orange-600 bg-orange-100'
    };
    return colorMap[status] || 'text-gray-600 bg-gray-100';
  }, []);

  return {
    canCancel,
    formatTime,
    formatDate,
    formatStatus,
    getStatusColor
  };
};
